import { useQueries, useQuery } from '@tanstack/react-query';
import { apiListContracts } from 'src/features/contracts/api/contracts.api';
import { apiListChangeOrders } from 'src/features/contracts/api/changeOrders.api';
import type { ChangeOrderRow } from 'src/types/changeOrder.type';

export interface ExtendedChangeOrderRow extends ChangeOrderRow {
    contractName: string;
    contractId: string;
    partyName: string;
}

export function useProjectAllChangeOrders(projectId: string) {
    // 1. 모든 계약 가져오기
    const contractsQuery = useQuery({
        queryKey: ['paymentContracts', projectId],
        queryFn: () => apiListContracts(projectId),
        staleTime: 30_000,
    });

    const contracts = contractsQuery.data ?? [];

    // 2. 각 계약별 CO 가져오기 (useQueries 사용)
    const coQueries = useQueries({
        queries: contracts.map((c) => ({
            queryKey: ['changeOrders', projectId, c.id],
            queryFn: async () => {
                const list = await apiListChangeOrders(projectId, c.id);
                return list.map((co) => ({
                    ...co,
                    contractId: c.id,
                    contractName: c.contractName,
                    partyName: (c as any).partyName ?? '—',
                })) as ExtendedChangeOrderRow[];
            },
            enabled: !!projectId && !!c.id,
        })),
    });

    const isLoading = contractsQuery.isLoading || coQueries.some((q) => q.isLoading);
    const error = contractsQuery.error || coQueries.find((q) => q.error)?.error;

    const allCOs = coQueries.flatMap((q) => q.data ?? []);

    // 정렬 (최신순 등)
    const sortedCOs = [...allCOs].sort((a, b) => {
        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return dateB - dateA;
    });

    return {
        data: sortedCOs,
        isLoading,
        error,
        refetch: () => {
            void contractsQuery.refetch();
            coQueries.forEach((q) => void q.refetch());
        },
    };
}
